// =============================================================================
// ui/tabs/RestoreCard.tsx — Settings → Backup & Restore → the two destructive
// whole-book controls.
// -----------------------------------------------------------------------------
// The screen half of `io/sync/restoreAccount.ts`. Read that file's header for
// what a restore can and cannot give back; this one is about saying so BEFORE
// the user commits, and about never claiming a success that did not happen.
//
// ── ⚠ A SEPARATE CARD FROM `YourDataCard`, DELIBERATELY ──
//
// Beta groups export, import and reset into one "Data" card
// (app/index.html:49796). Here the export lives in `YourDataCard`, which is
// SHARED with My Account — that sharing is the whole point of the card, and the
// duplicate export that got reverted is what it cost to learn. Import and Reset
// belong to Settings alone: beta's My Account has neither, and putting them in
// the shared card would place "erase everything" on the billing screen.
//
// ── 🚨 TWO PRESSES, AND THE SECOND ONE NAMES WHAT IT WILL DO ──
//
// Both controls follow the pattern the rest of this pane already uses (Sign
// Out, Clear All in Default Risk): the first press asks, the second acts, and
// the question states the consequence rather than asking "are you sure?".
//
// ── 🚨 AND NEITHER OF THEM RUNS IN THE DEMO ──
//
// Beta guards these because `saveStateNow` silently REFUSES while the demo is
// up, so Reset wiped the throwaway state, declined the cloud write and still
// said "Reset complete" — telling a user their account was erased when it was
// not (app/index.html:50283).
//
// ⚠ THIS CLIENT'S FAILURE MODE IS THE OPPOSITE, and the guard is still right.
// `io/demo/client.ts` THROWS on any write rather than no-opping, on purpose — so
// the lie beta had is impossible here. What is left without a guard is an
// exception surfacing as a raw error message, which is a poor way to say "leave
// the demo first". So the check is up front and explains itself.
// =============================================================================

import { useState } from 'react';
import { useSupabase } from '../state/supabase';
import { useDemo } from '../state/demo';
import { useAuthUser } from '../state/useAuthUser';
import {
  RESTORE_SKIPPED, resetEverything, restoreFromExport, validateImportFile,
  type ResetReport, type ValidatedFile, type WriteReport,
} from '../../io/sync/restoreAccount';
import { reportFailureAs } from '../../io/diagnostics/errorLog';

/** What the skipped tables mean on screen, as one sentence. */
function SkippedNote() {
  return (
    <p className="muted">
      {RESTORE_SKIPPED.map((s) => s.why).join('; ')}.
    </p>
  );
}

export function RestoreCard() {
  const { sb } = useSupabase();
  const demo = useDemo();
  const { user } = useAuthUser();

  const [file, setFile] = useState<ValidatedFile | null>(null);
  const [fileName, setFileName] = useState('');
  const [confirmReset, setConfirmReset] = useState(false);
  const [busy, setBusy] = useState<'import' | 'reset' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [imported, setImported] = useState<WriteReport | null>(null);
  const [reset, setReset] = useState<ResetReport | null>(null);

  const clearOutcome = () => { setError(null); setImported(null); setReset(null); };

  // ── Reading the file is NOT the destructive step ──
  // It parses, validates and reports — and then waits. Nothing is written until
  // the user has read what the file contains and pressed the second button.
  const chooseFile = async (picked: File) => {
    clearOutcome();
    setFile(null);
    setFileName(picked.name);
    try {
      const parsed = JSON.parse(await picked.text()) as unknown;
      setFile(validateImportFile(parsed, { id: user?.id ?? '' }));
    } catch (e) {
      setFile(null);
      setError(
        e instanceof SyntaxError
          ? 'That file isn’t valid JSON, so it can’t be a backup this app wrote.'
          : reportFailureAs('read backup file', e, 'That file could not be read.'),
      );
    }
  };

  const runImport = async () => {
    if (!file?.ok || !user) return;
    setBusy('import');
    clearOutcome();
    try {
      const report = await restoreFromExport(sb, { id: user.id }, file);
      setImported(report);
      // ⚠ The picked file is cleared only on a CLEAN run. After a partial
      // failure the button stays, because the outcome line says retrying is
      // safe — and it is, since the tables that did land are upserts. Taking
      // the control away would leave that advice with nothing to act on.
      if (!report.failures.length) { setFile(null); setFileName(''); }
    } catch (e) {
      setError(reportFailureAs('restore account', e, 'The restore could not be started.'));
    } finally {
      setBusy(null);
    }
  };

  const runReset = async () => {
    setBusy('reset');
    clearOutcome();
    setConfirmReset(false);
    try {
      setReset(await resetEverything(sb));
    } catch (e) {
      setError(reportFailureAs('reset everything', e, 'The reset could not be started.'));
    } finally {
      setBusy(null);
    }
  };

  // ── 🚨 THE DEMO BRANCH RENDERS NO CONTROLS AT ALL ──
  // Not disabled buttons: a greyed "Reset Everything" invites a click and then
  // explains itself, and this pane already prefers saying why over showing a
  // dead control (see `DefaultRiskEditor`'s catalogue branch).
  if (demo.active) {
    return (
      <section className="card">
        <h3>Restore &amp; reset</h3>
        <p className="muted">
          Leave the Demo Account to import a backup or reset your book. The demo
          is a sample that is never saved, so neither control would touch your
          real data — and doing nothing quietly is how a user ends up believing
          their account was erased when it was not.
        </p>
      </section>
    );
  }

  // ── ⚠ `status`, NOT a second `alert`, and that is not a detail ──
  // The export card above states this same cause on this same pane, with the
  // reason the auth service gave. A second `role="alert"` saying it again is one
  // announcement too many for a screen reader and one red box too many on
  // screen — so this states only the CONSEQUENCE for these two controls and
  // lets the card above make the claim. It also keeps "the alert on this pane"
  // a singular thing, which is what the existing coverage looks up.
  if (!user) {
    return (
      <section className="card">
        <h3>Restore &amp; reset</h3>
        <p className="red" role="status">
          Unavailable until your account can be read. Nothing that replaces or
          erases a whole book runs without knowing whose it is.
        </p>
      </section>
    );
  }

  const blocking = (file?.problems ?? []).filter((p) => p.severity === 'blocking');
  const warnings = (file?.problems ?? []).filter((p) => p.severity === 'warning');

  return (
    <section className="card">
      <h3>Restore &amp; reset</h3>
      <p className="muted">
        Replace everything here with a backup file, or erase it and start again.
        Both are permanent, and the only way back is a backup you already took.
      </p>
      <SkippedNote />

      {error ? <p className="red" role="alert">{error}</p> : null}

      {/* ── Import ───────────────────────────────────────────────────────── */}
      <div className="restore-block">
        <label className="btn" htmlFor="restore-file">Choose a backup file…</label>
        <input
          id="restore-file"
          type="file"
          accept="application/json,.json"
          className="restore-file-input"
          disabled={busy !== null}
          onChange={(e) => {
            const picked = e.target.files?.[0];
            // ⚠ Cleared so choosing the SAME file twice fires `change` again —
            // otherwise a user who fixes nothing and retries gets no response at
            // all, which reads as a broken button.
            e.target.value = '';
            if (picked) void chooseFile(picked);
          }}
        />

        {fileName ? <span className="muted restore-filename">{fileName}</span> : null}
      </div>

      {file ? (
        <div className="restore-summary">
          {blocking.length ? (
            <p className="red" role="alert">
              <b>This backup can’t be restored.</b>{' '}
              {blocking.map((p) => p.message).join(' ')}
            </p>
          ) : (
            <>
              {warnings.length ? (
                <p className="muted" role="status">{warnings.map((p) => p.message).join(' ')}</p>
              ) : null}
              {/* 🚨 THE CONFIRMATION NAMES THE TRADE. What arrives, and what
                  goes — a count on each side, because "replace everything" does
                  not tell the user whether the file they picked is the right
                  one. */}
              <p role="status">
                <b>{file.rowCount.toLocaleString()} rows</b> across{' '}
                {file.plan.length} table{file.plan.length === 1 ? '' : 's'} will
                replace what is here now. Anything not in this file is deleted.
              </p>
              <div className="row gap-8">
                <button type="button" className="btn" disabled={busy !== null}
                  onClick={() => { setFile(null); setFileName(''); }}>Cancel</button>
                <button type="button" className="btn danger" disabled={busy !== null}
                  onClick={() => void runImport()}>
                  {busy === 'import' ? 'Restoring…' : 'Replace my book with this file'}
                </button>
              </div>
            </>
          )}
        </div>
      ) : null}

      {/* ⚠ THE OUTCOME IS READ OFF THE REPORT, NEVER ASSUMED. A partial restore
          says so and names the tables, because the rows that did not land are
          the ones the user has to know about. */}
      {imported ? (
        <p className={imported.failures.length ? 'red' : 'muted'} role="status">
          {imported.failures.length ? (
            <>
              <b>Restored in part — {imported.failures.length} table
              {imported.failures.length === 1 ? '' : 's'} failed.</b>{' '}
              {imported.written.toLocaleString()} rows were written.{' '}
              {imported.failures.map((f) => `${f.table}: ${f.message}`).join(' · ')}.{' '}
              Your earlier rows in {imported.failures.length === 1 ? 'that table' : 'those tables'}{' '}
              are still here — running the import again is safe.
            </>
          ) : (
            <>
              Restored — {imported.written.toLocaleString()} rows written,{' '}
              {imported.pruned.toLocaleString()} removed. Reload to see it.
            </>
          )}
        </p>
      ) : null}

      {/* ── Reset ────────────────────────────────────────────────────────── */}
      <div className="restore-block">
        {confirmReset ? (
          <>
            <span className="muted restore-confirm">
              Erase every trade, journal entry, account, pattern, goal, review and
              backtest? This cannot be undone except from a backup.
            </span>
            <button type="button" className="btn" disabled={busy !== null}
              onClick={() => setConfirmReset(false)}>Cancel</button>
            <button type="button" className="btn danger" disabled={busy !== null}
              onClick={() => void runReset()}>
              {busy === 'reset' ? 'Erasing…' : 'Yes, erase everything'}
            </button>
          </>
        ) : (
          <button type="button" className="btn danger" disabled={busy !== null}
            onClick={() => { clearOutcome(); setConfirmReset(true); }}>
            Reset Everything
          </button>
        )}
      </div>

      {reset ? (
        <p className={reset.failures.length ? 'red' : 'muted'} role="status">
          {reset.failures.length ? (
            <>
              <b>Not everything was erased.</b>{' '}
              {reset.failures.map((f) => `${f.table}: ${f.message}`).join(' · ')}.{' '}
              {reset.cleared.length} of {reset.cleared.length + reset.failures.length}{' '}
              tables cleared — pressing Reset again is safe and will retry the rest.
            </>
          ) : (
            <>Erased — {reset.cleared.length} tables cleared. Reload to see it.</>
          )}
        </p>
      ) : null}
    </section>
  );
}
